import type mapboxgl from "mapbox-gl";
import React, { useCallback, useEffect, useRef, useState } from "react";
import ReactDOM from "react-dom";
import { toast } from "react-toastify";
import { useTranslation } from "react-i18next";
import { formatTranslation } from "../../../../shared/utils/translationUtils";
import type { LngLatPoint } from "../MapControlsPanel.types";

interface CoordinatesToolProps {
  mapRef: mapboxgl.Map | null;
  isActive: boolean;
  onCoordinatesChange?: (coords: LngLatPoint | null) => void;
  onCopyClick?: () => void;
}

/** Decimal degrees, as required by the acceptance criteria. */
const DECIMAL_DEGREE_PRECISION = 6;
const TOOLTIP_PRECISION = 4;

const CoordinatesTool: React.FC<CoordinatesToolProps> = ({
  mapRef,
  isActive,
  onCoordinatesChange,
  onCopyClick,
}) => {
  const { t } = useTranslation();
  const [coordinates, setCoordinates] = useState<LngLatPoint | null>(null);
  const [mousePosition, setMousePosition] = useState<{
    x: number;
    y: number;
  } | null>(null);

  const tooltipRef = useRef<HTMLDivElement>(null);
  const onCoordinatesChangeRef = useRef(onCoordinatesChange);
  const onCopyClickRef = useRef(onCopyClick);
  const translateRef = useRef(t);

  useEffect(() => {
    onCoordinatesChangeRef.current = onCoordinatesChange;
    onCopyClickRef.current = onCopyClick;
    translateRef.current = t;
  }, [onCoordinatesChange, onCopyClick, t]);

  const handleMouseMove = useCallback((e: mapboxgl.MapMouseEvent) => {
    const { lng, lat } = e.lngLat;
    setCoordinates({ lng, lat });
    onCoordinatesChangeRef.current?.({ lng, lat });

    const originalEvent = e.originalEvent as MouseEvent | undefined;
    if (originalEvent) {
      setMousePosition({
        x: originalEvent.clientX,
        y: originalEvent.clientY,
      });
    }
  }, []);

  const copyCoordinates = useCallback(
    async (lng: number, lat: number): Promise<void> => {
      const coordText = `${lng.toFixed(DECIMAL_DEGREE_PRECISION)} ${lat.toFixed(
        DECIMAL_DEGREE_PRECISION
      )}`;

      try {
        await navigator.clipboard.writeText(coordText);
        toast.success(
          formatTranslation(
            translateRef.current("newTrial.mapControls.coordinatesCopied")
          )
        );
        onCopyClickRef.current?.();
      } catch {
        toast.error(
          formatTranslation(
            translateRef.current("newTrial.mapControls.copyFailed")
          )
        );
      }
    },
    []
  );

  const handleMapClick = useCallback(
    (e: mapboxgl.MapMouseEvent): void => {
      void copyCoordinates(e.lngLat.lng, e.lngLat.lat);
    },
    [copyCoordinates]
  );

  useEffect(() => {
    if (!mapRef || !isActive) {
      setCoordinates(null);
      setMousePosition(null);
      onCoordinatesChangeRef.current?.(null);
      return;
    }

    mapRef.on("mousemove", handleMouseMove);
    mapRef.on("click", handleMapClick);
    mapRef.getCanvas().style.cursor = "crosshair";

    return () => {
      try {
        mapRef.off("mousemove", handleMouseMove);
        mapRef.off("click", handleMapClick);
        const canvas = mapRef.getCanvas();
        if (canvas) {
          canvas.style.cursor = "";
        }
      } catch {
        // Expected while the map is unmounting.
      }
    };
  }, [mapRef, isActive, handleMouseMove, handleMapClick]);

  useEffect(() => {
    if (tooltipRef.current && mousePosition) {
      tooltipRef.current.style.setProperty(
        "--tooltip-x",
        `${mousePosition.x + 10}px`
      );
      tooltipRef.current.style.setProperty(
        "--tooltip-y",
        `${mousePosition.y - 30}px`
      );
    }
  }, [mousePosition]);

  if (!isActive || !coordinates || !mousePosition) {
    return null;
  }

  const tooltip = (
    <div
      ref={tooltipRef}
      className="coordinates-tooltip-marker"
      data-testid="coordinates-tooltip"
    >
      {coordinates.lng.toFixed(TOOLTIP_PRECISION)},{" "}
      {coordinates.lat.toFixed(TOOLTIP_PRECISION)}
    </div>
  );

  return ReactDOM.createPortal(tooltip, document.body);
};

export default CoordinatesTool;
